import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";

interface StyleProps {
  dark: boolean;
  secondaryColor: string;
}

export const getVerificationStyles = ({ dark, secondaryColor }: StyleProps) => {
  const colors = {
    background: dark ? "#000" : "#F8FAFC",

    card: dark ? "#111" : "#F8FAFC",

    text: dark ? "#fff" : "#111827",

    muted: dark ? "#94A3B8" : "#64748B",

    input: dark ? "#18181B" : "#ebeef1",

    border: dark ? "#334155" : "#CBD5E1",
  };

  return {
    colors,

    container: {
      width: "100%",

      minHeight: "100%",

      display: "flex",

      justifyContent: "center",

      alignItems: "center",
    } satisfies SxProps<Theme>,

    card: {
      width: "100%",

      maxWidth: {
        xs: "100%",
        sm: 500,
      },
      mt: 2,

      p: 2,

      borderRadius: {
        xs: 3,
        sm: 5,
      },

      textAlign: "center",

      backgroundColor: colors.card,

      boxShadow: "none",
    } satisfies SxProps<Theme>,

    icon: {
      width: {
        xs: 60,
        sm: 82,
      },

      height: {
        xs: 60,
        sm: 82,
      },

      mx: "auto",

      mb: {
        xs: 2,
        sm: 3,
      },

      borderRadius: "50%",

      display: "flex",

      justifyContent: "center",

      alignItems: "center",

      backgroundColor: alpha(secondaryColor, 0.3),
    } satisfies SxProps<Theme>,

    subtitle: {
      color: colors.muted,

      mb: 2,

      fontSize: {
        xs: 13,
        sm: 16,
      },
    } satisfies SxProps<Theme>,

    email: {
      display: "inline-flex",

      maxWidth: "100%",

      mt: 2,

      px: {
        xs: 1.5,
        sm: 3,
      },

      py: 1,

      mb: {
        xs: 3,
        sm: 4,
      },

      borderRadius: 50,

      overflow: "hidden",

      textOverflow: "ellipsis",

      whiteSpace: "nowrap",

      backgroundColor: colors.input,
    } satisfies SxProps<Theme>,

    codeContainer: {
      width: "100%",

      display: "flex",

      justifyContent: "center",

      alignItems: "center",

      gap: {
        xs: 0.5,
        sm: 1,
      },

      flexWrap: {
        xs: "wrap",
        sm: "nowrap",
      },

      mb: 3,
    } satisfies SxProps<Theme>,

    input: (digit: string) =>
      ({
        width: {
          xs: 34,
          sm: 45,
        },

        "& input": {
          textAlign: "center",

          fontSize: {
            xs: 16,
            sm: 22,
          },

          fontWeight: 700,

          color: colors.text,

          p: 0,
        },

        "& .MuiOutlinedInput-root": {
          width: "100%",

          height: {
            xs: 40,
            sm: 50,
          },

          borderRadius: 3,

          backgroundColor: colors.input,

          "& fieldset": {
            borderColor: digit ? secondaryColor : colors.border,
          },

          "&.Mui-focused fieldset": {
            borderColor: secondaryColor,

            borderWidth: 2,
          },
        },
      }) satisfies SxProps<Theme>,

    timer: {
      display: "inline-flex",

      alignItems: "center",

      px: {
        xs: 1.5,
        sm: 2,
      },

      py: 1,

      mb: 3,

      borderRadius: 50,

      fontSize: {
        xs: 11,
        sm: 14,
      },

      border: `1px solid ${secondaryColor}`,

      color: secondaryColor,
    } satisfies SxProps<Theme>,

    button: {
      width: "100%",

      mb: {
        xs: 1,
        sm: 2,
      },

      fontWeight: 600,
    } satisfies SxProps<Theme>,

    buttonConfirm: {
      width: "100%",
    } satisfies SxProps<Theme>,

    error: {
      width: "100%",

      mt: 2,

      mb: 2,

      px: {
        xs: 1.5,
        sm: 2,
      },

      py: 1.5,

      borderRadius: 3,

      backgroundColor: dark ? "rgba(244,67,54,.12)" : "rgba(244,67,54,.08)",

      border: "1px solid rgba(244,67,54,.35)",

      color: dark ? "#FCA5A5" : "#DC2626",

      textAlign: "center",

      fontSize: {
        xs: 13,
        sm: 14,
      },
    } satisfies SxProps<Theme>,
  };
};
